import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const todayIso = () => new Date().toISOString().slice(0, 10);

const LoanBalances = () => {
  const [searchParams] = useSearchParams();
  const selectedLoanParam = searchParams.get("loanId") || "";
  const isSingleLoanView = Boolean(selectedLoanParam);
  const [loans, setLoans] = useState([]);
  const [balances, setBalances] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    LoanID: selectedLoanParam,
    BalanceDate: todayIso(),
    BalanceAmount: "",
  });

  const sortedLoans = useMemo(
    () => [...loans].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [loans]
  );
  const selectedLoan = useMemo(
    () => loans.find((loan) => String(loan.LoanID) === String(form.LoanID)) || null,
    [loans, form.LoanID]
  );
  const visibleBalances = useMemo(() => {
    const rows = isSingleLoanView
      ? balances.filter((row) => String(row.LoanID) === String(selectedLoanParam))
      : balances;
    return [...rows].sort(
      (left, right) =>
        String(right.BalanceDate || "").localeCompare(String(left.BalanceDate || "")) ||
        Number(right.LoanBalanceID || 0) - Number(left.LoanBalanceID || 0)
    );
  }, [balances, isSingleLoanView, selectedLoanParam]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loansRes, balancesRes, currenciesRes] = await Promise.all([
          apiClient.get("/loans"),
          apiClient.get("/loan-balances"),
          apiClient.get("/currencies"),
        ]);
        const loanRows = Array.isArray(loansRes.data) ? loansRes.data : [];
        const balanceRows = Array.isArray(balancesRes.data) ? balancesRes.data : [];
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        const defaultLoanId = selectedLoanParam || loanRows[0]?.LoanID || "";

        setLoans(loanRows);
        setBalances(balanceRows);
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
        setForm((current) => ({
          ...current,
          LoanID: isSingleLoanView ? selectedLoanParam : current.LoanID || defaultLoanId,
        }));
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load loan balances.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSingleLoanView, selectedLoanParam]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setFormError("");
    setForm((current) => ({
      LoanID: isSingleLoanView ? selectedLoanParam : current.LoanID || sortedLoans[0]?.LoanID || "",
      BalanceDate: todayIso(),
      BalanceAmount: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!form.LoanID || !form.BalanceDate || form.BalanceAmount === "") {
      setFormError("Loan, balance date, and balance amount are required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        LoanID: form.LoanID,
        BalanceDate: form.BalanceDate,
        BalanceAmount: form.BalanceAmount,
      };

      if (editingId) {
        await apiClient.put(`/loan-balances/${editingId}`, payload);
      } else {
        await apiClient.post("/loan-balances", payload);
      }

      const balancesRes = await apiClient.get("/loan-balances");
      setBalances(Array.isArray(balancesRes.data) ? balancesRes.data : []);
      resetForm();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Could not save loan balance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.LoanBalanceID);
    setFormError("");
    setForm({
      LoanID: String(row.LoanID || ""),
      BalanceDate: row.BalanceDate || todayIso(),
      BalanceAmount: row.BalanceAmount != null ? String(row.BalanceAmount) : "",
    });
  };

  const handleDelete = async (loanBalanceId) => {
    try {
      await apiClient.delete(`/loan-balances/${loanBalanceId}`);
      setBalances((current) => current.filter((row) => row.LoanBalanceID !== loanBalanceId));
      if (editingId === loanBalanceId) {
        resetForm();
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not delete loan balance.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Loan Balances</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Loans">Loans</Link> / Balances
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Loans", to: "/Loans" },
          { label: selectedLoan ? `${selectedLoan.Description} Balances` : "Loan Balances" },
        ]}
        note={selectedLoan ? `Add a new balance line or edit previous entries for ${selectedLoan.Description}.` : "Enter or revise dated loan balances."}
      />

      {error && <p className="notice notice-error">{error}</p>}

      <section className="panel form-card">
        <form className="form" onSubmit={handleSubmit}>
          {isSingleLoanView ? (
            <>
              <label>Loan</label>
              <div className="form-value-preview">
                {selectedLoan?.Description || "Selected loan"}
                {selectedLoan?.LenderName ? ` (${selectedLoan.LenderName})` : ""}
              </div>
            </>
          ) : (
            <>
              <label htmlFor="loan-balance-loan">Loan</label>
              <select id="loan-balance-loan" name="LoanID" value={form.LoanID} onChange={handleChange}>
                <option value="">Select loan</option>
                {sortedLoans.map((loan) => (
                  <option key={loan.LoanID} value={loan.LoanID}>
                    {loan.Description} {loan.LenderName ? `(${loan.LenderName})` : ""}
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="loan-balance-date">Balance Date</label>
          <input
            id="loan-balance-date"
            type="date"
            name="BalanceDate"
            value={form.BalanceDate}
            onChange={handleChange}
          />

          <label htmlFor="loan-balance-amount">Loan Balance</label>
          <input
            id="loan-balance-amount"
            type="number"
            step="0.01"
            name="BalanceAmount"
            value={form.BalanceAmount}
            onChange={handleChange}
            placeholder="e.g. 250000.00"
          />
          <div className="form-value-preview">
            Display: {formatMoney(form.BalanceAmount || 0, defaultCurrency || "Default")}
          </div>

          <div className="form-actions">
            <button className="btn" type="submit" disabled={saving}>
              {editingId ? "Update Balance" : "Save Balance"}
            </button>
            {editingId ? (
              <button className="btn btn-secondary" type="button" onClick={resetForm}>
                Cancel Edit
              </button>
            ) : null}
          </div>
          {formError && <p className="notice notice-error">{formError}</p>}
        </form>
      </section>

      {loading ? (
        <p className="notice notice-muted">Loading loan balances...</p>
      ) : (
        <section className="resource-list">
          {visibleBalances.length === 0 ? (
            <p className="notice notice-muted">
              {isSingleLoanView && selectedLoan
                ? `No balances recorded yet for ${selectedLoan.Description}. Add the first balance line above.`
                : "No loan balances recorded yet."}
            </p>
          ) : (
            visibleBalances.map((row) => (
              <article key={row.LoanBalanceID} className="resource-row">
                <div>
                  <strong>{row.LoanDescription}</strong>
                  <div className="resource-meta">Lender: {row.LenderName || "-"}</div>
                  <div className="resource-meta">Balance date: {row.BalanceDate}</div>
                  <div className="resource-meta">
                    Balance: {formatMoney(row.BalanceAmount, defaultCurrency || "Default")}
                  </div>
                </div>
                <div className="actions">
                  <button className="btn btn-secondary" type="button" onClick={() => handleEdit(row)}>
                    Update
                  </button>
                  <button className="btn btn-danger" type="button" onClick={() => handleDelete(row.LoanBalanceID)}>
                    Delete
                  </button>
                </div>
              </article>
            ))
          )}
        </section>
      )}
    </div>
  );
};

export default LoanBalances;
